// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Dropdown, TextArea as FinTextArea, StatusBanner } from '@loa/ui-components';
import { adminApi } from '../api/adminApi';
import type { AdvisorWithAdmin, RelationshipReassignRequest } from '../types/admin';
import type { Relationship } from '../types/relationship';

interface ReassignRelationshipModalProps {
  open: boolean;
  relationship: Relationship | null;
  currentAdvisorId?: string;
  onClose: () => void;
  onSuccess: (updatedRelationship: Relationship) => void;
}

const ReassignRelationshipModal: React.FC<ReassignRelationshipModalProps> = ({
  open,
  relationship,
  currentAdvisorId,
  onClose,
  onSuccess,
}) => {
  const [advisors, setAdvisors] = useState<AdvisorWithAdmin[]>([]);
  const [newAdvisorId, setNewAdvisorId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAdvisors, setLoadingAdvisors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAdvisors = useCallback(async () => {
    try {
      setLoadingAdvisors(true);
      const response = await adminApi.listAdvisors({ status: 'active' }, 0, 100);
      setAdvisors(response.content);
    } catch (err) {
      console.error('Failed to load advisors:', err);
      setError('Failed to load advisors. Please try again.');
    } finally {
      setLoadingAdvisors(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadAdvisors();
      setNewAdvisorId('');
      setReason('');
      setError(null);
    }
  }, [open, loadAdvisors]);

  const handleSubmit = async () => {
    if (!relationship || !newAdvisorId) return;

    try {
      setLoading(true);
      setError(null);

      const request: RelationshipReassignRequest = {
        newAdvisorId,
        reason: reason || undefined,
      };

      const updatedRelationship = await adminApi.reassignRelationship(relationship.id, request);
      onSuccess(updatedRelationship);
      onClose();
    } catch (err) {
      console.error('Failed to reassign relationship:', err);
      setError('Failed to reassign relationship. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const availableAdvisors = advisors
    .filter((a) => String(a.id) !== currentAdvisorId)
    .map((a) => ({
      value: String(a.id),
      text: `${a.firstName} ${a.lastName} (${a.email}) - ${a.profileCount || 0} relationships`,
    }));

  return (
    <Modal open={open} onClose={onClose} size="medium">
      <Modal.Header icon="exchange" title={`Reassign Relationship: ${relationship?.profileName}`} onClose={onClose} />
      <Modal.Content>
        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Dropdown
            label="Transfer To"
            placeholder={loadingAdvisors ? 'Loading advisors...' : 'Select an advisor...'}
            options={availableAdvisors}
            value={newAdvisorId}
            onChange={(value) => setNewAdvisorId(value)}
            disabled={loadingAdvisors || loading}
            search
            required
            fluid
          />
          <FinTextArea
            label="Reason for Reassignment (Optional)"
            placeholder="Enter reason for reassigning this relationship..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            fluid
          />
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
        primaryAction={{
          label: 'Reassign Relationship',
          onClick: () => { void handleSubmit(); },
          loading,
          disabled: !newAdvisorId || loading,
          iconLeft: 'exchange',
        }}
      />
    </Modal>
  );
};

export default ReassignRelationshipModal;
